import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popover } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useDispatch, useSelector } from 'react-redux';
import { DatePickerPopOver } from '../DatePickerUI/datePickerPopOver';
import {
  AUDIENCE_ANALYSIS,
  COMPARISON_PERIOD,
  OVERALL_PERFORMANCE,
  OVERALL_PERFORMANCE_SUB_BRANCH,
  TABLE_PERIOD_COMPARE,
  TABLE_YTD,
} from '../../../constants/datePickerConstants';
import { TransformDate } from '../DatePickerUtils/datePickerUtils';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { calendarActions } from '../../../modules/reducer/calendarSlice';

const ComparisonPeriodPicker = ({
  component,
  comparePeriod,
  tableType = 'none',
  combineCompare = false,
  combine,
  branchPerformanceButtonStyle = false,
}) => {
  const [anchorElForPopOver, setAnchorElForPopOver] = useState(null);
  const [btnText, setBtnText] = useState('');
  const { compareOverallPerformanceRangeTableOne, compareOverallPerformanceRangeYTD } = useSelector(
    (state) => state.overallPerformance
  );
  const {
    compareOverallPerformanceSubBranchRangeTableOne,
    compareOverallPerformanceSubBranchRangeYTD,
  } = useSelector((state) => state.overallPerformanceSubBranch);
  const { compareAudienceAnalysisRange } = useSelector((state) => state.audienceAnalysis);
  const [weeks, setWeeks] = useState([]);
  const [comparisonPeriodPicker, setComparisonPeriodPicker] = useState(false);
  const { transformedDataWithDate, quarterDataForCompare } = useSelector(
    selectors.datePickerCombinedData
  );
  const dispatch = useDispatch();

  const highlightComparisonSelectedWeeks = () => {
    if (transformedDataWithDate?.length > 0) {
      switch (component) {
        case OVERALL_PERFORMANCE: {
          if (tableType === TABLE_PERIOD_COMPARE) {
            const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
              `w${compareOverallPerformanceRangeTableOne?.start_week}`,
              transformedDataWithDate?.find(
                (data) => data?.currYear === compareOverallPerformanceRangeTableOne?.start_year
              )
            );
            dispatch(calendarActions?.setComparisonQuarter(currentQuarter));
            dispatch(
              calendarActions?.setComparisonYear(compareOverallPerformanceRangeTableOne?.start_year)
            );
          }
          if (tableType === TABLE_YTD) {
            const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
              `w${compareOverallPerformanceRangeYTD?.start_week}`,
              transformedDataWithDate?.find(
                (data) => data?.currYear === compareOverallPerformanceRangeYTD?.start_year
              )
            );
            dispatch(calendarActions?.setComparisonQuarter(currentQuarter));
            dispatch(
              calendarActions?.setComparisonYear(compareOverallPerformanceRangeYTD?.start_year)
            );
          }
          break;
        }
        case OVERALL_PERFORMANCE_SUB_BRANCH: {
          if (tableType === TABLE_PERIOD_COMPARE) {
            const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
              `w${compareOverallPerformanceSubBranchRangeTableOne?.start_week}`,
              transformedDataWithDate?.find(
                (data) =>
                  data?.currYear === compareOverallPerformanceSubBranchRangeTableOne?.start_year
              )
            );
            dispatch(calendarActions?.setComparisonQuarter(currentQuarter));
            dispatch(
              calendarActions?.setComparisonYear(
                compareOverallPerformanceSubBranchRangeTableOne?.start_year
              )
            );
          }
          if (tableType === TABLE_YTD) {
            const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
              `w${compareOverallPerformanceSubBranchRangeYTD?.start_week}`,
              transformedDataWithDate?.find(
                (data) => data?.currYear === compareOverallPerformanceSubBranchRangeYTD?.start_year
              )
            );
            dispatch(calendarActions?.setComparisonQuarter(currentQuarter));
            dispatch(
              calendarActions?.setComparisonYear(
                compareOverallPerformanceSubBranchRangeYTD?.start_year
              )
            );
          }
          break;
        }
        case AUDIENCE_ANALYSIS: {
          const currentQuarter = TransformDate.getCurrentQuarterBasedOnWeek(
            `w${compareAudienceAnalysisRange?.start_week}`,
            transformedDataWithDate?.find(
              (data) => data?.currYear === compareAudienceAnalysisRange?.start_year
            )
          );
          dispatch(calendarActions?.setComparisonQuarter(currentQuarter));
          dispatch(calendarActions?.setComparisonYear(compareAudienceAnalysisRange?.start_year));
          break;
        }
      }
    }
  };

  const handleClickForPopOver = (event) => {
    if (btnText === COMPARISON_PERIOD) return null;
    if (!quarterDataForCompare) return null;
    highlightComparisonSelectedWeeks();
    setComparePeriodText();
    setAnchorElForPopOver(event.currentTarget);
    setComparisonPeriodPicker(true);
  };

  const handleCloseForPopOver = () => {
    setAnchorElForPopOver(null);
  };

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  const styles = {
    parentDiv: {
      position: 'relative',
      bottom: '5px',
    },
    pickerHeader: {
      margin: '0',
      display: 'block',
      padding: '0',
      color: 'rgba(0, 0, 0, 0.6)',
      lineHeight: '1.9em',
      fontSize: '0.8rem',
    },
  };

  const setComparePeriodText = () => {
    switch (component) {
      case AUDIENCE_ANALYSIS: {
        const defaultAudienceWeekSelection =
          compareAudienceAnalysisRange &&
          TransformDate.preDefinedWeekRange(compareAudienceAnalysisRange);
        setWeeks(defaultAudienceWeekSelection);
        break;
      }
      case OVERALL_PERFORMANCE:
        // added the default week selection based on YTD table and comparision table for the overall performance tab
        if (tableType === TABLE_PERIOD_COMPARE) {
          const defaultOverallWeekSelection =
            compareOverallPerformanceRangeTableOne &&
            TransformDate.preDefinedWeekRange(compareOverallPerformanceRangeTableOne);
          setWeeks(defaultOverallWeekSelection);
        }
        if (tableType === TABLE_YTD) {
          const defaultOverallWeekSelection =
            compareOverallPerformanceRangeYTD &&
            TransformDate.preDefinedWeekRange(compareOverallPerformanceRangeYTD);
          setWeeks(defaultOverallWeekSelection);
        }
        break;
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        // added the default week selection based on YTD table and comparision table for the overall performance tab
        if (tableType === TABLE_PERIOD_COMPARE) {
          const defaultOverallWeekSelection =
            compareOverallPerformanceSubBranchRangeTableOne &&
            TransformDate.preDefinedWeekRange(compareOverallPerformanceSubBranchRangeTableOne);
          setWeeks(defaultOverallWeekSelection);
        }
        if (tableType === TABLE_YTD) {
          const defaultOverallWeekSelection =
            compareOverallPerformanceSubBranchRangeYTD &&
            TransformDate.preDefinedWeekRange(compareOverallPerformanceSubBranchRangeYTD);
          setWeeks(defaultOverallWeekSelection);
        }
        break;
    }
  };

  useEffect(() => {
    switch (component) {
      case AUDIENCE_ANALYSIS: {
        const audienceAnalysisPrev = checkForGlobalObject(compareAudienceAnalysisRange);
        if (!audienceAnalysisPrev) {
          const endWeek = TransformDate.getEndWeek(
            compareAudienceAnalysisRange?.end_week,
            compareAudienceAnalysisRange?.end_year
          );
          const value = `w${compareAudienceAnalysisRange?.start_week}, ${compareAudienceAnalysisRange?.start_year} - w${endWeek || compareAudienceAnalysisRange?.end_week}, ${compareAudienceAnalysisRange?.end_year}`;
          setBtnText(value);
        } else {
          setBtnText(COMPARISON_PERIOD);
        }
        break;
      }
      case OVERALL_PERFORMANCE:
        if (tableType === TABLE_PERIOD_COMPARE) {
          const overallPerformancePrev = checkForGlobalObject(
            compareOverallPerformanceRangeTableOne
          );
          if (!overallPerformancePrev) {
            const endWeek = TransformDate.getEndWeek(
              compareOverallPerformanceRangeTableOne?.end_week,
              compareOverallPerformanceRangeTableOne?.end_year
            );
            const value = `w${compareOverallPerformanceRangeTableOne?.start_week}, ${compareOverallPerformanceRangeTableOne?.start_year} - w${endWeek || compareOverallPerformanceRangeTableOne?.end_week}, ${compareOverallPerformanceRangeTableOne?.end_year}`;
            setBtnText(value);
          } else {
            setBtnText(COMPARISON_PERIOD);
          }
        }
        if (tableType === TABLE_YTD) {
          const overallPerformancePrev = checkForGlobalObject(compareOverallPerformanceRangeYTD);
          const endWeek = TransformDate.getEndWeek(
            compareOverallPerformanceRangeYTD?.end_week,
            compareOverallPerformanceRangeYTD?.end_year
          );
          if (!overallPerformancePrev) {
            const value = `w${compareOverallPerformanceRangeYTD?.start_week}, ${compareOverallPerformanceRangeYTD?.start_year} - w${endWeek || compareOverallPerformanceRangeYTD?.end_week}, ${compareOverallPerformanceRangeYTD?.end_year}`;
            setBtnText(value);
          } else {
            setBtnText(COMPARISON_PERIOD);
          }
        }
        break;
      case OVERALL_PERFORMANCE_SUB_BRANCH:
        if (tableType === TABLE_PERIOD_COMPARE) {
          const overallPerformancePrev = checkForGlobalObject(
            compareOverallPerformanceSubBranchRangeTableOne
          );
          if (!overallPerformancePrev) {
            const endWeek = TransformDate.getEndWeek(
              compareOverallPerformanceSubBranchRangeTableOne?.end_week,
              compareOverallPerformanceSubBranchRangeTableOne?.end_year
            );
            const value = `w${compareOverallPerformanceSubBranchRangeTableOne?.start_week}, ${compareOverallPerformanceSubBranchRangeTableOne?.start_year} - w${endWeek || compareOverallPerformanceSubBranchRangeTableOne?.end_week}, ${compareOverallPerformanceSubBranchRangeTableOne?.end_year}`;
            setBtnText(value);
          } else {
            setBtnText(COMPARISON_PERIOD);
          }
        }
        if (tableType === TABLE_YTD) {
          const overallPerformancePrev = checkForGlobalObject(
            compareOverallPerformanceSubBranchRangeYTD
          );
          const endWeek = TransformDate.getEndWeek(
            compareOverallPerformanceSubBranchRangeYTD?.end_week,
            compareOverallPerformanceSubBranchRangeYTD?.end_year
          );
          if (!overallPerformancePrev) {
            const value = `w${compareOverallPerformanceSubBranchRangeYTD?.start_week}, ${compareOverallPerformanceSubBranchRangeYTD?.start_year} - w${endWeek || compareOverallPerformanceSubBranchRangeYTD?.end_week}, ${compareOverallPerformanceSubBranchRangeYTD?.end_year}`;
            setBtnText(value);
          } else {
            setBtnText(COMPARISON_PERIOD);
          }
        }
        break;

      default:
    }
  }, [
    compareOverallPerformanceRangeTableOne,
    btnText,
    component,
    compareAudienceAnalysisRange,
    compareOverallPerformanceRangeYTD,
    tableType,
    comparePeriod,
    compareOverallPerformanceSubBranchRangeYTD,
    compareOverallPerformanceSubBranchRangeTableOne,
  ]);

  const openPopOver = Boolean(anchorElForPopOver);

  const popoverContent = (
    <DatePickerPopOver
      onClose={handleCloseForPopOver}
      compareWeekSelection={weeks}
      component={component}
      type="compare"
      tableType={tableType}
      comparisonPeriodPicker={comparisonPeriodPicker}
    />
  );

  const getButtonStyle = () => {
    if (combineCompare) {
      return {};
    }
    if (branchPerformanceButtonStyle) {
      return { backgroundColor: '#FFFFFF', color: '#000000' };
    }
    return { color: 'white', border: '0.3px solid white' };
  };

  const getStartIcon = () => {
    if (!branchPerformanceButtonStyle) {
      return <CompareArrowsIcon />;
    }
    return (
      <SyncAltIcon
        style={
          branchPerformanceButtonStyle ? { transform: 'rotate(90deg)', height: '0.8em' } : null
        }
      />
    );
  };

  return (
    <div style={combine ? { position: 'relative', bottom: '5px' } : {}}>
      {combine && <p style={styles.pickerHeader}>Comparison Period</p>}
      <Button
        id="timePeriod"
        variant="outlined"
        color="secondary"
        startIcon={getStartIcon()}
        onClick={handleClickForPopOver}
        style={getButtonStyle()}
      >
        {btnText}
      </Button>
      <Popover
        open={openPopOver}
        anchorEl={anchorElForPopOver}
        onClose={handleCloseForPopOver}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {popoverContent}
      </Popover>
    </div>
  );
};

// Prop validation
ComparisonPeriodPicker.propTypes = {
  component: PropTypes.element,
  comparePeriod: PropTypes.object,
  tableType: PropTypes.string,
  combineCompare: PropTypes.bool,
  combine: PropTypes.bool,
  branchPerformanceButtonStyle: PropTypes.bool,
};

export default ComparisonPeriodPicker;
